// path: dashboard/src/pages/OperationsAssociate/ProcurementPlanning.jsx

"use client"

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Eye, Check, ShoppingCart, Package, Filter, Search, Calendar, User, Trash2, X, FileText, AlertCircle } from "lucide-react";
import MasterSideBar from "../../components/MasterSideBar";
import Topbar from "../../components/TopbarNav";
import { fetchPlanningProjectsWithoutProcurementPlan } from "../../api/projectApi";
import { getRequirementsByProjectId, updateRequirement } from "../../api/requirementApi";
import CreateProcurementPlanModal from "../../components/operationsAssociate/ProcurementPlanModal";
import { motion, AnimatePresence } from "framer-motion";

export default function ProjectProcurementPlanning() {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [requirements, setRequirements] = useState([]);
  const [procurementItems, setProcurementItems] = useState([]);
  const [decisions, setDecisions] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [activeRequirement, setActiveRequirement] = useState(null);
  const [activeModal, setActiveModal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState("");
  
  // Sidebar states
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const capitalize = (str) => {
    if (!str) return "-";
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const loadProjects = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const projectsData = await fetchPlanningProjectsWithoutProcurementPlan();
      setProjects(projectsData || []);
    } catch (err) {
      setError("System failed to aggregate planning data. Check connectivity.");
      console.error(err);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError("Session expired. Please re-authenticate.");
      setInitialLoading(false);
      return;
    }
    loadProjects();
  }, [loadProjects]);

  useEffect(() => {
    if (!selectedProject) return;
    const loadRequirements = async () => {
      try {
        const data = await getRequirementsByProjectId(selectedProject);
        const normalizedData = data.map(r => ({
          ...r,
          specification: r.specification || '',
          availabilityStatus: r.availabilityStatus || 'Pending'
        }));
        setRequirements(normalizedData);

        const initialDecisions = {};
        const initialProcureItems = [];
        normalizedData.forEach(r => {
          if (r.availabilityStatus && r.availabilityStatus !== 'Pending') {
            initialDecisions[r._id] = r.availabilityStatus;
            if (r.availabilityStatus === 'Procure') initialProcureItems.push(r);
          }
        });
        setDecisions(initialDecisions);
        setProcurementItems(initialProcureItems);
      } catch (err) {
        console.error("Failed to fetch requirements:", err);
      }
    };
    loadRequirements();
  }, [selectedProject]);

  const selectedProjectData = projects.find(p => p._id === selectedProject);

  const filteredRequirements = useMemo(() => {
    let reqs = requirements;
    if (searchTerm) {
      reqs = reqs.filter(r => r.name.toLowerCase().includes(searchTerm.toLowerCase()));
    }
    if (statusFilter !== "all") {
      reqs = reqs.filter(r => {
        const decision = decisions[r._id];
        if (statusFilter === "pending") return !decision;
        return decision === statusFilter;
      });
    }
    return reqs;
  }, [requirements, searchTerm, statusFilter, decisions]);

  const totals = useMemo(() => ({
    totalItems: procurementItems.length,
    totalValue: procurementItems.reduce((sum, item) => sum + (item.totalCost || 0), 0)
  }), [procurementItems]);

  const handleDecision = async (req, decision) => {
    const validStatuses = ['Available', 'Procure', 'Pending'];
    if (!validStatuses.includes(decision)) return;

    setDecisions(prev => ({ ...prev, [req._id]: decision }));
    setRequirements(prev => prev.map(r => r._id === req._id ? { ...r, availabilityStatus: decision } : r));

    if (decision === "Procure") {
      setProcurementItems(prev => prev.find(item => item._id === req._id) ? prev : [...prev, req]);
    } else {
      setProcurementItems(prev => prev.filter(item => item._id !== req._id));
    }

    try {
      await updateRequirement(req._id, { availabilityStatus: decision });
    } catch (err) {
      console.error("Sync error:", err);
    }
  };

  const removeProcurementItem = (item) => handleDecision(item, 'Pending');

  const openModal = (req = null, type = null) => {
    if (req) {
      setActiveRequirement(req);
      setActiveModal("requirement");
    } else {
      setActiveModal(type || "procurement");
    }
  };

  const closeModal = async (planCreated = false) => {
    setActiveRequirement(null);
    setActiveModal(null);
    if (planCreated) {
      setProjects(prev => prev.filter(p => p._id !== selectedProject));
      setSelectedProject(null);
      setRequirements([]);
      setProcurementItems([]);
      setDecisions({});
      setTimeout(loadProjects, 500);
    }
  };

  const displayValue = (val, fallback = "-") => val ? (typeof val === "object" ? JSON.stringify(val) : val) : fallback;

  return (
    <div className="flex h-screen bg-gray-50 font-sans overflow-hidden">
      <MasterSideBar 
        visible={sidebarOpen} 
        onClose={() => setSidebarOpen(false)} 
        collapsed={sidebarCollapsed}
        setCollapsed={setSidebarCollapsed}
        onLogout={() => { localStorage.clear(); window.location.href = "/"; }}
      />

      <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-0 md:ml-64'}`}>
        <Topbar onSidebarToggle={() => setSidebarOpen(true)} />

        <main className="flex-1 overflow-y-auto p-4 md:p-8 mt-16 lg:mt-20">
          <div className="max-w-7xl mx-auto space-y-8">
            
            {/* Header */}
            <header className="flex flex-col md:flex-row justify-between items-start gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-black text-gray-800 tracking-tight uppercase">
                  Procurement <span className="text-orange-600">Planning</span>
                </h1>
                <p className="text-gray-500 text-xs font-bold uppercase tracking-widest mt-1 italic">Logistics Phase: Requirement Assessment</p>
              </div>
            </header>

            {error && (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-xl flex items-center gap-3">
                <AlertCircle className="text-red-500" />
                <p className="text-red-800 font-bold text-xs uppercase tracking-tight">{error}</p>
              </div>
            )}

            {/* Project Selection Container */}
            <section className="bg-white rounded-[2rem] shadow-sm border border-gray-100 overflow-hidden">
              <div className="p-6 md:p-8">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  <div className="space-y-4">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Select Target Project</label>
                    <select
                      className="w-full bg-gray-50 border-none rounded-2xl px-5 py-4 text-sm font-bold text-gray-800 focus:ring-2 focus:ring-orange-500 transition-all cursor-pointer"
                      value={selectedProject || ""}
                      onChange={(e) => setSelectedProject(e.target.value)}
                    >
                      <option value="">-- Active Planning List --</option>
                      {projects.map((p) => (
                        <option key={p._id} value={p._id}>{displayValue(p.projectTitle)}</option>
                      ))}
                    </select>
                  </div>

                  {selectedProjectData && (
                    <div className="lg:col-span-2">
                      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-orange-50/50 border border-orange-100 rounded-[1.5rem] p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-1">
                          <p className="text-[10px] font-black text-orange-600 uppercase tracking-widest italic">Project Title</p>
                          <h3 className="font-black text-gray-800 leading-tight">{displayValue(selectedProjectData.projectTitle)}</h3>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-[9px] font-black text-gray-400 uppercase tracking-tighter">Donor</p>
                            <p className="text-xs font-bold text-gray-700 truncate">{displayValue(selectedProjectData.donor?.map(d => d.name || d).join(", "), "HTCH-UN")}</p>
                          </div>
                          <div>
                            <p className="text-[9px] font-black text-gray-400 uppercase tracking-tighter">Timeline</p>
                            <p className="text-xs font-bold text-gray-700">{displayValue(selectedProjectData.startDate?.split("T")[0])}</p>
                          </div>
                        </div>
                      </motion.div>
                    </div>
                  )}
                </div>
              </div>
            </section>

            {selectedProject && (
              <>
                {/* Requirements Table Section */}
                <section className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden animate-fadeIn">
                  <div className="p-6 border-b border-gray-50 flex flex-col md:flex-row justify-between items-center gap-4">
                    <h2 className="text-xs font-black text-gray-800 uppercase tracking-widest flex items-center gap-2">
                      <Package className="text-orange-500" size={16} /> Needs Assessment Table
                    </h2>
                    <div className="flex gap-2 w-full md:w-auto">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                        <input
                          type="text"
                          placeholder="Filter Items..."
                          className="w-full pl-9 pr-4 py-2 bg-gray-50 border-none rounded-xl text-xs font-bold focus:ring-1 focus:ring-orange-500"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>
                      <select
                        className="bg-gray-50 border-none rounded-xl text-xs font-bold px-4 py-2 focus:ring-1 focus:ring-orange-500"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                      >
                        <option value="all">All</option>
                        <option value="Procure">To Procure</option>
                        <option value="Available">Available</option>
                      </select>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="bg-gray-50/50">
                          <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Description</th>
                          <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Qty/Unit</th>
                          <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Est. Cost</th>
                          <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Decision Matrix</th>
                          <th className="px-6 py-4"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {filteredRequirements.map(req => (
                          <tr key={req._id} className="hover:bg-gray-50/50 transition-colors group">
                            <td className="px-6 py-4">
                              <p className="text-sm font-black text-gray-800">{displayValue(req.name)}</p>
                              <p className="text-[9px] font-bold text-gray-400 uppercase tracking-tighter">{capitalize(req.type)}</p>
                            </td>
                            <td className="px-6 py-4">
                              <p className="text-sm font-bold text-gray-700">{req.quantity} <span className="text-[10px] text-gray-400">{req.unit}</span></p>
                            </td>
                            <td className="px-6 py-4">
                              <p className="text-sm font-black text-orange-600">${req.totalCost?.toLocaleString()}</p>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleDecision(req, "Available")}
                                  className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-tighter transition-all ${decisions[req._id] === "Available" ? "bg-emerald-500 text-white shadow-md shadow-emerald-100" : "bg-emerald-50 text-emerald-600 hover:bg-emerald-100"}`}
                                >
                                  Available
                                </button>
                                <button
                                  onClick={() => handleDecision(req, "Procure")}
                                  className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-tighter transition-all ${decisions[req._id] === "Procure" ? "bg-orange-500 text-white shadow-md shadow-orange-100" : "bg-orange-50 text-orange-600 hover:bg-orange-100"}`}
                                >
                                  Procure
                                </button>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-right">
                              <button onClick={() => openModal(req)} className="p-2 text-gray-300 hover:text-orange-600 transition-colors">
                                <Eye size={18} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>

                {/* Procurement Queue */}
                <section className="bg-white rounded-[2.5rem] p-8 text-white shadow-2xl">
                  <div className="flex justify-between items-center mb-8">
                    <div>
                      <h2 className="text-xs font-black uppercase tracking-[0.3em] text-orange-400">Final Procurement Queue</h2>
                      <p className="text-gray-400 text-[10px] mt-1 italic">Ready for Vendor Bidding & Purchase Orders</p>
                    </div>
                    <ShoppingCart className="text-gray-700" size={32} />
                  </div>

                  {procurementItems.length > 0 ? (
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {procurementItems.map(item => (
                          <div key={item._id} className="bg-white/5 border border-white/10 rounded-2xl p-4 flex justify-between items-center group">
                            <div>
                              <p className="text-xs font-black uppercase tracking-tight">{item.name}</p>
                              <p className="text-[10px] text-gray-500 font-bold italic">${item.totalCost?.toLocaleString()}</p>
                            </div>
                            <button onClick={() => removeProcurementItem(item)} className="p-2 text-gray-600 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                      
                      <div className="pt-6 border-t border-white/10 flex flex-col sm:flex-row justify-between items-center gap-6">
                        <div className="flex gap-8">
                          <div>
                            <p className="text-[9px] font-black text-gray-500 uppercase">Item Count</p>
                            <p className="text-xl font-black">{totals.totalItems}</p>
                          </div>
                          <div>
                            <p className="text-[9px] font-black text-gray-500 uppercase">Aggregated Budget</p>
                            <p className="text-xl font-black text-orange-500">${totals.totalValue?.toLocaleString()}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => openModal(null, "procurement")}
                          className="w-full sm:w-auto bg-orange-600 hover:bg-orange-500 text-white font-black text-[10px] uppercase tracking-[0.2em] px-10 py-4 rounded-2xl shadow-xl transition-all active:scale-95"
                        >
                          Generate Procurement Plan
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12 border-2 border-dashed border-white/5 rounded-3xl">
                      <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest italic">Matrix Empty: No Items Marked for Procurement</p>
                    </div>
                  )}
                </section>
              </>
            )}
          </div>
        </main>
      </div>

      {/* --- Modals & Overlays --- */}
      <AnimatePresence>
        {activeModal === "procurement" && selectedProjectData && (
          <CreateProcurementPlanModal
            isOpen={true}
            onClose={closeModal}
            project={selectedProjectData}
            itemsToProcure={procurementItems}
          />
        )}

        {activeModal === "requirement" && activeRequirement && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 backdrop-blur-md bg-black/40">
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white rounded-[2rem] shadow-2xl w-full max-w-xl overflow-hidden">
              <div className="bg-gray-50 p-6 border-b border-gray-100 flex items-center justify-between">
                <h3 className="text-xs font-black text-gray-800 uppercase tracking-widest">Requirement Blueprint</h3>
                <button onClick={() => closeModal(false)} className="text-gray-400 hover:text-gray-900 transition-colors"><X size={20} /></button>
              </div>
              <div className="p-8 space-y-6">
                <h2 className="text-xl font-black text-gray-900 border-b border-orange-100 pb-4">{displayValue(activeRequirement.name)}</h2>
                <div className="grid grid-cols-2 gap-y-4">
                   {[
                    ["Classification", capitalize(activeRequirement.type)],
                    ["Matrix Status", activeRequirement.availabilityStatus],
                    ["Quantity", `${activeRequirement.quantity} ${activeRequirement.unit}`],
                    ["Financials", `$${activeRequirement.totalCost?.toLocaleString()}`]
                  ].map(([label, value]) => (
                    <div key={label}>
                      <p className="text-[9px] font-black text-gray-400 uppercase tracking-tighter">{label}</p>
                      <p className="text-sm font-bold text-gray-800">{value}</p>
                    </div>
                  ))}
                  <div className="col-span-2 mt-2">
                    <p className="text-[9px] font-black text-gray-400 uppercase tracking-tighter">Detailed Specification</p>
                    <p className="text-xs text-gray-600 leading-relaxed italic mt-1">{displayValue(activeRequirement.specification)}</p>
                  </div>
                </div>
                <button onClick={() => closeModal(false)} className="w-full py-4 bg-gray-900 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest mt-4">Dismiss</button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}